import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import CrudModal from "components/crud/CrudModal";
import CrudInput from "components/crud/CrudInput";
import CrudSelect from "components/crud/CrudSelect";

import {
  createEmployee,
  updateEmployee,
  getEmployees,
  getEmployeeDocuments,
  uploadEmployeeDocument,
  deleteEmployeeDocument,
  getEmployeeDocumentUrl,
} from "services/api/employeeApi";

import { getRoles } from "services/api/roleApi";
import { getOrganizations } from "services/api/organizationApi";
import api from "services/api/axios";

import {
  showSuccess,
  showError,
} from "utils/toast";

/**
 * ============================================================
 * CONSTANTS
 * ============================================================
 */

const DEFAULT_FORM = {
  name: "",
  email: "",
  phone: "",
  employeeCode: "",
  designation: "",
  roleSlug: "",

  reportingManagerId: "",

  headquarterId: "",
  workingStationId: "",
  stayPointId: "",
  territoryIds: [],

  joiningDate: "",
  leavingDate: "",
  aadhaarNumber: "",

  // Permanent Address
  permanentAddressLine1: "",
  permanentAddressLine2: "",
  permanentCity: "",
  permanentState: "",
  permanentPincode: "",
  permanentCountry: "India",

  // Correspondence Address
  correspondenceAddressLine1: "",
  correspondenceAddressLine2: "",
  correspondenceCity: "",
  correspondenceState: "",
  correspondencePincode: "",
  correspondenceCountry: "India",

  // TA / DA & Reimbursement Policy
  daLocalWorking: "100",
  daOutstationFooding: "800",
  daOutstationLodging: "500",
  daOutstationSameDayReturn: "300",
  localTwoWheelerPerKm: "4.50",
  outstationTwoWheelerPerKm: "4.50",
  localCarPerKm: "10.00",
  outstationCarPerKm: "10.00",
  mobileInternetMonthly: "350",

  password: "",
  status: "active",
};

const DEFAULT_DOCUMENT_FORM = {
  documentType: "aadhaar",
  documentNumber: "",
  remarks: "",
  file: null,
};

const DOCUMENT_TYPES = [
  { value: "aadhaar", label: "Aadhaar" },
  { value: "pan", label: "PAN" },
  { value: "id_proof", label: "ID Proof" },
  { value: "address_proof", label: "Address Proof" },
  { value: "joining_document", label: "Joining Document" },
  { value: "other", label: "Other" },
];

const EMPLOYEE_STATUSES = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "probition", label: "Probation Period" },
  { value: "resigned", label: "Resigned" },
  { value: "terminated", label: "Terminated" },
];

const DOCUMENT_ACCEPT = ".jpg,.jpeg,.png,.webp,.pdf";
const MAX_DOCUMENT_SIZE = 10 * 1024 * 1024;

const ALLOWED_DOCUMENT_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
];

/**
 * ============================================================
 * HELPERS
 * ============================================================
 */

const normalizeId = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return String(value);
};

const normalizeIds = (values) => {
  if (!Array.isArray(values)) {
    return [];
  }

  return values
    .filter(
      (value) =>
        value !== null &&
        value !== undefined &&
        value !== ""
    )
    .map((value) => String(value?.id ?? value));
};

const formatDateForInput = (value) => {
  if (!value) {
    return "";
  }

  const stringValue = String(value);

  return stringValue.length >= 10
    ? stringValue.substring(0, 10)
    : stringValue;
};

const extractRoles = (response) =>
  response?.data?.roles ||
  response?.data?.data?.roles ||
  response?.roles ||
  response?.data ||
  [];

const extractEmployees = (response) =>
  response?.data?.employees ||
  response?.data?.data?.employees ||
  response?.data?.records ||
  response?.data?.data?.records ||
  [];

const extractRecords = (response) => {
  /*
   * organizationApi.getOrganizations() returns response.data from Axios.
   * Your actual API response is:
   *
   * {
   *   status: true,
   *   data: {
   *     items: [...],
   *     pagination: {...}
   *   }
   * }
   *
   * So the master records are response.data.items.
   */
  if (Array.isArray(response)) {
    return response;
  }

  const records =
    response?.data?.items ||
    response?.data?.states ||
    response?.data?.districts ||
    response?.data?.cities ||
    response?.data?.zones ||
    response?.data?.areas ||
    response?.data?.beats ||
    response?.data?.territories ||
    response?.data?.records ||
    response?.data?.data?.items ||
    response?.data?.data?.states ||
    response?.data?.data?.districts ||
    response?.data?.data?.cities ||
    response?.data?.data?.zones ||
    response?.data?.data?.areas ||
    response?.data?.data?.beats ||
    response?.data?.data?.territories ||
    response?.data?.data?.records ||
    response?.items ||
    response?.states ||
    response?.districts ||
    response?.cities ||
    response?.zones ||
    response?.areas ||
    response?.beats ||
    response?.territories ||
    response?.records ||
    response?.data;

  if (Array.isArray(records)) {
    return records;
  }

  if (records && typeof records === "object" && records.id) {
    return [records];
  }

  if (response && typeof response === "object" && response.id) {
    return [response];
  }

  return [];
};

const getEmployeeRoleSlug = (employee) => {
  if (
    Array.isArray(employee?.roles) &&
    employee.roles.length > 0
  ) {
    return (
      employee.roles[0]?.slug ||
      employee.roles[0]?.roleSlug ||
      ""
    );
  }

  if (employee?.role?.slug) {
    return employee.role.slug;
  }

  return employee?.roleSlug || "";
};

const getAddressValue = (
  employee,
  section,
  field
) => {
  const nested = employee?.[section];

  if (
    nested &&
    nested[field] !== undefined &&
    nested[field] !== null
  ) {
    return nested[field];
  }

  return employee?.[field] ?? "";
};

const buildEditForm = (employee) => {
  const user = employee?.user || {};

  const territoryData =
    employee?.territories ||
    employee?.territoryIds ||
    [];

  return {
    name: user.name || employee?.name || "",
    email: user.email || employee?.email || "",
    phone: user.phone || employee?.phone || "",

    employeeCode: employee?.employeeCode || "",
    designation: employee?.designation || "",
    roleSlug: getEmployeeRoleSlug(employee),

    reportingManagerId: normalizeId(
      employee?.reportingManagerId ||
        employee?.reportingManager?.id
    ),

    headquarterId: normalizeId(
      employee?.headquarterId ||
        employee?.headquarter?.id
    ),

    workingStationId: normalizeId(
      employee?.workingStationId ||
        employee?.workingStation?.id
    ),

    stayPointId: normalizeId(
      employee?.stayPointId ||
        employee?.stayPoint?.id
    ),

    territoryIds: normalizeIds(territoryData),

    joiningDate: formatDateForInput(
      employee?.joiningDate
    ),

    leavingDate: formatDateForInput(
      employee?.leavingDate
    ),

    aadhaarNumber:
      employee?.aadhaarNumber || "",

    permanentAddressLine1: getAddressValue(
      employee,
      "permanentAddress",
      "addressLine1"
    ),
    permanentAddressLine2: getAddressValue(
      employee,
      "permanentAddress",
      "addressLine2"
    ),
    permanentCity: getAddressValue(
      employee,
      "permanentAddress",
      "city"
    ),
    permanentState: getAddressValue(
      employee,
      "permanentAddress",
      "state"
    ),
    permanentPincode: getAddressValue(
      employee,
      "permanentAddress",
      "pincode"
    ),
    permanentCountry:
      getAddressValue(
        employee,
        "permanentAddress",
        "country"
      ) || "India",

    correspondenceAddressLine1:
      getAddressValue(
        employee,
        "correspondenceAddress",
        "addressLine1"
      ),
    correspondenceAddressLine2:
      getAddressValue(
        employee,
        "correspondenceAddress",
        "addressLine2"
      ),
    correspondenceCity: getAddressValue(
      employee,
      "correspondenceAddress",
      "city"
    ),
    correspondenceState: getAddressValue(
      employee,
      "correspondenceAddress",
      "state"
    ),
    correspondencePincode: getAddressValue(
      employee,
      "correspondenceAddress",
      "pincode"
    ),
    correspondenceCountry:
      getAddressValue(
        employee,
        "correspondenceAddress",
        "country"
      ) || "India",

    daLocalWorking:
      employee?.daLocalWorking != null
        ? String(employee.daLocalWorking)
        : "100",

    daOutstationFooding:
      employee?.daOutstationFooding != null
        ? String(employee.daOutstationFooding)
        : "800",

    daOutstationLodging:
      employee?.daOutstationLodging != null
        ? String(employee.daOutstationLodging)
        : "500",

    daOutstationSameDayReturn:
      employee?.daOutstationSameDayReturn != null
        ? String(employee.daOutstationSameDayReturn)
        : "300",

    localTwoWheelerPerKm:
      employee?.localTwoWheelerPerKm != null
        ? String(employee.localTwoWheelerPerKm)
        : "4.50",

    outstationTwoWheelerPerKm:
      employee?.outstationTwoWheelerPerKm != null
        ? String(employee.outstationTwoWheelerPerKm)
        : "4.50",

    localCarPerKm:
      employee?.localCarPerKm != null
        ? String(employee.localCarPerKm)
        : "10.00",

    outstationCarPerKm:
      employee?.outstationCarPerKm != null
        ? String(employee.outstationCarPerKm)
        : "10.00",

    mobileInternetMonthly:
      employee?.mobileInternetMonthly != null
        ? String(employee.mobileInternetMonthly)
        : "350",

    password: "",
    status: employee?.status || "active",
  };
};

const findByName = (records, value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (!Array.isArray(records) || records.length === 0) {
    return null;
  }

  const target = String(value).trim().toLowerCase();

  return (
    records.find((record) => {
      const candidates = [
        record?.id,
        record?.name,
        record?.code,
        record?.value,
        record?.slug,
      ];

      return candidates.some(
        (candidate) =>
          candidate !== null &&
          candidate !== undefined &&
          String(candidate).trim().toLowerCase() === target
      );
    }) || null
  );
};

/**
 * ============================================================
 * COMPONENT
 * ============================================================
 */

export default function EmployeeModal({
  open,
  onClose,
  reload,
  item,
}) {
  const isEdit = Boolean(item);

  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] =
    useState(false);

  const [roles, setRoles] = useState([]);
  const [managers, setManagers] = useState([]);

  const [headquarters, setHeadquarters] =
    useState([]);
  const [workingStations, setWorkingStations] =
    useState([]);
  const [stayPoints, setStayPoints] =
    useState([]);
  const [territories, setTerritories] =
    useState([]);

  /**
   * Address master data.
   *
   * Employee DB stores address names/text, not master IDs.
   * IDs are used only in the frontend for dependent dropdowns.
   */
  const [countries, setCountries] = useState([]);
  const [permanentStates, setPermanentStates] =
    useState([]);
  const [permanentDistricts, setPermanentDistricts] =
    useState([]);
  const [permanentCities, setPermanentCities] =
    useState([]);

  const [
    correspondenceStates,
    setCorrespondenceStates,
  ] = useState([]);
  const [
    correspondenceDistricts,
    setCorrespondenceDistricts,
  ] = useState([]);
  const [
    correspondenceCities,
    setCorrespondenceCities,
  ] = useState([]);

  const [permanentCountryId, setPermanentCountryId] =
    useState("");
  const [permanentStateId, setPermanentStateId] =
    useState("");
  const [permanentDistrictId, setPermanentDistrictId] =
    useState("");

  const [
    correspondenceCountryId,
    setCorrespondenceCountryId,
  ] = useState("");
  const [
    correspondenceStateId,
    setCorrespondenceStateId,
  ] = useState("");
  const [
    correspondenceDistrictId,
    setCorrespondenceDistrictId,
  ] = useState("");

  const [addressLoading, setAddressLoading] =
    useState(false);

  const [sameAsPermanent, setSameAsPermanent] =
    useState(false);

  const [form, setForm] = useState({
    ...DEFAULT_FORM,
  });

  const [documents, setDocuments] = useState([]);
  const [documentLoading, setDocumentLoading] =
    useState(false);
  const [documentUploading, setDocumentUploading] =
    useState(false);

  const [documentForm, setDocumentForm] =
    useState({
      ...DEFAULT_DOCUMENT_FORM,
    });

  /**
   * ==========================================================
   * LOAD EMPLOYEE DROPDOWNS
   * ==========================================================
   */

  const loadDropdowns = useCallback(async () => {
    try {
      setDropdownLoading(true);

      const [
        roleResponse,
        employeeResponse,
        headquarterResponse,
        workingStationResponse,
        stayPointResponse,
        territoryResponse,
        countryResponse,
      ] = await Promise.all([
        getRoles(),
        getEmployees(1, 500),
        getOrganizations(
          "headquarters",
          1,
          500
        ),
        getOrganizations(
          "working-stations",
          1,
          500
        ),
        getOrganizations(
          "stay-points",
          1,
          500
        ),
        getOrganizations(
          "territories",
          1,
          500
        ),
        getOrganizations(
          "countries",
          1,
          1000
        ),
      ]);

      setRoles(
        Array.isArray(extractRoles(roleResponse))
          ? extractRoles(roleResponse)
          : []
      );

      setManagers(
        Array.isArray(
          extractEmployees(employeeResponse)
        )
          ? extractEmployees(employeeResponse)
          : []
      );

      setHeadquarters(
        Array.isArray(
          extractRecords(headquarterResponse)
        )
          ? extractRecords(headquarterResponse)
          : []
      );

      setWorkingStations(
        Array.isArray(
          extractRecords(
            workingStationResponse
          )
        )
          ? extractRecords(
              workingStationResponse
            )
          : []
      );

      setStayPoints(
        Array.isArray(
          extractRecords(stayPointResponse)
        )
          ? extractRecords(
              stayPointResponse
            )
          : []
      );

      setTerritories(
        Array.isArray(
          extractRecords(territoryResponse)
        )
          ? extractRecords(territoryResponse)
          : []
      );

      setCountries(
        Array.isArray(
          extractRecords(countryResponse)
        )
          ? extractRecords(countryResponse)
          : []
      );
    } catch (error) {
      console.error(
        "Employee dropdown loading error:",
        error
      );

      setRoles([]);
      setManagers([]);
      setHeadquarters([]);
      setWorkingStations([]);
      setStayPoints([]);
      setTerritories([]);
      setCountries([]);

      showError(
        error?.response?.data?.message ||
          "Unable to load employee options."
      );
    } finally {
      setDropdownLoading(false);
    }
  }, []);

  /**
   * ==========================================================
   * ADDRESS MASTER LOADERS
   * ==========================================================
   */

  const loadStates = useCallback(
    async (countryId, type) => {
      if (!countryId) {
        if (type === "permanent") {
          setPermanentStates([]);
          setPermanentDistricts([]);
          setPermanentCities([]);
        } else {
          setCorrespondenceStates([]);
          setCorrespondenceDistricts([]);
          setCorrespondenceCities([]);
        }

        return;
      }

      try {
        setAddressLoading(true);

        const response = await api.get(
          `/organization/countries/${countryId}/states`
        );

        const data = extractRecords(response?.data);

        if (type === "permanent") {
          setPermanentStates(
            Array.isArray(data) ? data : []
          );
        } else {
          setCorrespondenceStates(
            Array.isArray(data) ? data : []
          );
        }
      } catch (error) {
        console.error(
          "State loading error:",
          error
        );

        if (type === "permanent") {
          setPermanentStates([]);
        } else {
          setCorrespondenceStates([]);
        }

        showError(
          error?.response?.data?.message ||
            "Unable to load states."
        );
      } finally {
        setAddressLoading(false);
      }
    },
    []
  );

  const loadDistricts = useCallback(
    async (stateId, type) => {
      if (!stateId) {
        if (type === "permanent") {
          setPermanentDistricts([]);
          setPermanentCities([]);
        } else {
          setCorrespondenceDistricts([]);
          setCorrespondenceCities([]);
        }

        return;
      }

      try {
        setAddressLoading(true);

        const response = await api.get(
          `/organization/states/${stateId}/districts`
        );

        const data = extractRecords(response?.data);

        if (type === "permanent") {
          setPermanentDistricts(
            Array.isArray(data) ? data : []
          );
        } else {
          setCorrespondenceDistricts(
            Array.isArray(data) ? data : []
          );
        }
      } catch (error) {
        console.error(
          "District loading error:",
          error
        );

        if (type === "permanent") {
          setPermanentDistricts([]);
        } else {
          setCorrespondenceDistricts([]);
        }

        showError(
          error?.response?.data?.message ||
            "Unable to load districts."
        );
      } finally {
        setAddressLoading(false);
      }
    },
    []
  );

  const loadCities = useCallback(
    async (districtId, type) => {
      if (!districtId) {
        if (type === "permanent") {
          setPermanentCities([]);
        } else {
          setCorrespondenceCities([]);
        }

        return;
      }

      try {
        setAddressLoading(true);

        const response = await api.get(
          `/organization/districts/${districtId}/cities`
        );

        const data = extractRecords(response?.data);

        if (type === "permanent") {
          setPermanentCities(
            Array.isArray(data) ? data : []
          );
        } else {
          setCorrespondenceCities(
            Array.isArray(data) ? data : []
          );
        }
      } catch (error) {
        console.error(
          "City loading error:",
          error
        );

        if (type === "permanent") {
          setPermanentCities([]);
        } else {
          setCorrespondenceCities([]);
        }

        showError(
          error?.response?.data?.message ||
            "Unable to load cities."
        );
      } finally {
        setAddressLoading(false);
      }
    },
    []
  );

  /**
   * ==========================================================
   * INITIALIZE ADDRESS CASCADE IN EDIT MODE
   * ==========================================================
   */

  const initializeAddressCascade = useCallback(
    async (employee) => {
      const permanentCountry =
        getAddressValue(
          employee,
          "permanentAddress",
          "country"
        ) || "India";

      const permanentState =
        getAddressValue(
          employee,
          "permanentAddress",
          "state"
        );

      const permanentCity =
        getAddressValue(
          employee,
          "permanentAddress",
          "city"
        );

      const correspondenceCountry =
        getAddressValue(
          employee,
          "correspondenceAddress",
          "country"
        ) || "India";

      const correspondenceState =
        getAddressValue(
          employee,
          "correspondenceAddress",
          "state"
        );

      const correspondenceCity =
        getAddressValue(
          employee,
          "correspondenceAddress",
          "city"
        );

      const permanentCountryRecord =
        findByName(
          countries,
          permanentCountry
        );

      const correspondenceCountryRecord =
        findByName(
          countries,
          correspondenceCountry
        );

      if (permanentCountryRecord?.id) {
        const countryId = String(
          permanentCountryRecord.id
        );

        setPermanentCountryId(countryId);

        const stateResponse = await api.get(
          `/organization/countries/${countryId}/states`
        );

        const states = extractRecords(
          stateResponse?.data
        );

        setPermanentStates(
          Array.isArray(states) ? states : []
        );

        const stateRecord = findByName(
          states,
          permanentState
        );

        if (stateRecord?.id) {
          const stateId = String(
            stateRecord.id
          );

          setPermanentStateId(stateId);

          const districtResponse = await api.get(
            `/organization/states/${stateId}/districts`
          );

          const districts = extractRecords(
            districtResponse?.data
          );

          setPermanentDistricts(
            Array.isArray(districts)
              ? districts
              : []
          );

          /*
           * City is stored in Employee as city text.
           * We find the district containing that city.
           * First try the selected district list; if the city
           * cannot be resolved there, keep the city text intact.
           */
          let districtRecord = null;

          if (permanentCity) {
            for (
              const district of Array.isArray(
                districts
              )
                ? districts
                : []
            ) {
              const cityResponse = await api.get(
                `/organization/districts/${String(
                  district.id
                )}/cities`
              );

              const cities = extractRecords(
                cityResponse?.data
              );

              const cityRecord =
                findByName(
                  cities,
                  permanentCity
                );

              if (cityRecord) {
                districtRecord =
                  district;

                setPermanentCities(
                  Array.isArray(cities)
                    ? cities
                    : []
                );

                break;
              }
            }
          }

          if (districtRecord?.id) {
            const districtId = String(
              districtRecord.id
            );

            setPermanentDistrictId(
              districtId
            );

            const cityResponse = await api.get(
              `/organization/districts/${districtId}/cities`
            );

            const cities = extractRecords(
              cityResponse?.data
            );

            setPermanentCities(
              Array.isArray(cities)
                ? cities
                : []
            );
          }
        }
      }

      if (
        correspondenceCountryRecord?.id
      ) {
        const countryId = String(
          correspondenceCountryRecord.id
        );

        setCorrespondenceCountryId(
          countryId
        );

        const stateResponse = await api.get(
          `/organization/countries/${countryId}/states`
        );

        const states = extractRecords(
          stateResponse?.data
        );

        setCorrespondenceStates(
          Array.isArray(states)
            ? states
            : []
        );

        const stateRecord = findByName(
          states,
          correspondenceState
        );

        if (stateRecord?.id) {
          const stateId = String(
            stateRecord.id
          );

          setCorrespondenceStateId(
            stateId
          );

          const districtResponse = await api.get(
            `/organization/states/${stateId}/districts`
          );

          const districts = extractRecords(
            districtResponse?.data
          );

          setCorrespondenceDistricts(
            Array.isArray(districts)
              ? districts
              : []
          );

          let districtRecord = null;

          if (correspondenceCity) {
            for (
              const district of Array.isArray(
                districts
              )
                ? districts
                : []
            ) {
              const cityResponse = await api.get(
                `/organization/districts/${String(
                  district.id
                )}/cities`
              );

              const cities = extractRecords(
                cityResponse?.data
              );

              const cityRecord =
                findByName(
                  cities,
                  correspondenceCity
                );

              if (cityRecord) {
                districtRecord =
                  district;

                setCorrespondenceCities(
                  Array.isArray(cities)
                    ? cities
                    : []
                );

                break;
              }
            }
          }

          if (districtRecord?.id) {
            const districtId = String(
              districtRecord.id
            );

            setCorrespondenceDistrictId(
              districtId
            );

            const cityResponse = await api.get(
              `/organization/districts/${districtId}/cities`
            );

            const cities = extractRecords(
              cityResponse?.data
            );

            setCorrespondenceCities(
              Array.isArray(cities)
                ? cities
                : []
            );
          }
        }
      }
    },
    [countries]
  );

  /**
   * ==========================================================
   * LOAD DOCUMENTS
   * ==========================================================
   */

  const loadDocuments = useCallback(
    async (employeeId) => {
      if (!employeeId) {
        setDocuments([]);
        return;
      }

      try {
        setDocumentLoading(true);

        const response =
          await getEmployeeDocuments(
            employeeId
          );

        const documentData =
          response?.data?.documents ||
          response?.data?.data?.documents ||
          [];

        setDocuments(
          Array.isArray(documentData)
            ? documentData
            : []
        );
      } catch (error) {
        console.error(
          "Employee documents loading error:",
          error
        );

        setDocuments([]);

        showError(
          error?.response?.data?.message ||
            "Unable to load employee documents."
        );
      } finally {
        setDocumentLoading(false);
      }
    },
    []
  );

  /**
   * ==========================================================
   * INITIALIZE MODAL
   * ==========================================================
   */

  useEffect(() => {
    if (!open) {
      return;
    }

    setDocumentForm({
      ...DEFAULT_DOCUMENT_FORM,
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setSameAsPermanent(false);

    setPermanentCountryId("");
    setPermanentStateId("");
    setPermanentDistrictId("");

    setCorrespondenceCountryId("");
    setCorrespondenceStateId("");
    setCorrespondenceDistrictId("");

    setPermanentStates([]);
    setPermanentDistricts([]);
    setPermanentCities([]);

    setCorrespondenceStates([]);
    setCorrespondenceDistricts([]);
    setCorrespondenceCities([]);

    if (item) {
      setForm(buildEditForm(item));
      loadDocuments(item.id);
    } else {
      setForm({
        ...DEFAULT_FORM,
      });
      setDocuments([]);
    }

    loadDropdowns();
  }, [
    open,
    item,
    loadDropdowns,
    loadDocuments,
  ]);

  /**
   * Initialize address after countries are loaded
   * for edit mode.
   */
  useEffect(() => {
    if (!open || !item || !countries.length) {
      return;
    }

    initializeAddressCascade(item);
  }, [
    open,
    item,
    countries,
    initializeAddressCascade,
  ]);

  /**
   * ==========================================================
   * FORM CHANGE
   * ==========================================================
   */

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * ==========================================================
   * TERRITORY CHANGE
   * ==========================================================
   */

  const handleTerritoryChange = (event) => {
    const selectedValues = Array.from(
      event.target.selectedOptions || []
    ).map((option) =>
      String(option.value)
    );

    setForm((previous) => ({
      ...previous,
      territoryIds: selectedValues,
    }));
  };

  /**
   * ==========================================================
   * PERMANENT ADDRESS HANDLERS
   * ==========================================================
   */

  const handlePermanentCountryChange = async (
    event
  ) => {
    const countryId = String(
      event.target.value || ""
    );

    const country = countries.find(
      (item) =>
        String(item.id) === countryId
    );

    setPermanentCountryId(countryId);
    setPermanentStateId("");
    setPermanentDistrictId("");

    setPermanentStates([]);
    setPermanentDistricts([]);
    setPermanentCities([]);

    setForm((previous) => ({
      ...previous,
      permanentCountry:
        country?.name || "",
      permanentState: "",
      permanentCity: "",
    }));

    await loadStates(
      countryId,
      "permanent"
    );
  };

  const handlePermanentStateChange = async (
    event
  ) => {
    const stateId = String(
      event.target.value || ""
    );

    const state = permanentStates.find(
      (item) =>
        String(item.id) === stateId
    );

    setPermanentStateId(stateId);
    setPermanentDistrictId("");

    setPermanentDistricts([]);
    setPermanentCities([]);

    setForm((previous) => ({
      ...previous,
      permanentState:
        state?.name || "",
      permanentCity: "",
    }));

    await loadDistricts(
      stateId,
      "permanent"
    );
  };

  const handlePermanentDistrictChange =
    async (event) => {
      const districtId = String(
        event.target.value || ""
      );

      setPermanentDistrictId(
        districtId
      );

      setPermanentCities([]);

      const district =
        permanentDistricts.find(
          (item) =>
            String(item.id) ===
            districtId
        );

      setForm((previous) => ({
        ...previous,
        permanentCity: "",
      }));

      if (districtId) {
        await loadCities(
          districtId,
          "permanent"
        );
      }
    };

  const handlePermanentCityChange = (
    event
  ) => {
    const cityId = String(
      event.target.value || ""
    );

    const city = permanentCities.find(
      (item) =>
        String(item.id) === cityId
    );

    setForm((previous) => ({
      ...previous,
      permanentCity:
        city?.name || "",
    }));
  };

  /**
   * ==========================================================
   * CORRESPONDENCE ADDRESS HANDLERS
   * ==========================================================
   */

  const handleCorrespondenceCountryChange =
    async (event) => {
      const countryId = String(
        event.target.value || ""
      );

      const country = countries.find(
        (item) =>
          String(item.id) ===
          countryId
      );

      setCorrespondenceCountryId(
        countryId
      );

      setCorrespondenceStateId("");
      setCorrespondenceDistrictId("");

      setCorrespondenceStates([]);
      setCorrespondenceDistricts([]);
      setCorrespondenceCities([]);

      setForm((previous) => ({
        ...previous,
        correspondenceCountry:
          country?.name || "",
        correspondenceState: "",
        correspondenceCity: "",
      }));

      await loadStates(
        countryId,
        "correspondence"
      );
    };

  const handleCorrespondenceStateChange =
    async (event) => {
      const stateId = String(
        event.target.value || ""
      );

      const state =
        correspondenceStates.find(
          (item) =>
            String(item.id) ===
            stateId
        );

      setCorrespondenceStateId(
        stateId
      );

      setCorrespondenceDistrictId("");

      setCorrespondenceDistricts([]);
      setCorrespondenceCities([]);

      setForm((previous) => ({
        ...previous,
        correspondenceState:
          state?.name || "",
        correspondenceCity: "",
      }));

      await loadDistricts(
        stateId,
        "correspondence"
      );
    };

  const handleCorrespondenceDistrictChange =
    async (event) => {
      const districtId = String(
        event.target.value || ""
      );

      setCorrespondenceDistrictId(
        districtId
      );

      setCorrespondenceCities([]);

      setForm((previous) => ({
        ...previous,
        correspondenceCity: "",
      }));

      await loadCities(
        districtId,
        "correspondence"
      );
    };

  const handleCorrespondenceCityChange =
    (event) => {
      const cityId = String(
        event.target.value || ""
      );

      const city =
        correspondenceCities.find(
          (item) =>
            String(item.id) ===
            cityId
        );

      setForm((previous) => ({
        ...previous,
        correspondenceCity:
          city?.name || "",
      }));
    };

  /**
   * ==========================================================
   * SAME AS PERMANENT
   * ==========================================================
   */

  const handleSameAsPermanent = async (
    event
  ) => {
    const checked = event.target.checked;

    setSameAsPermanent(checked);

    if (!checked) {
      return;
    }

    setForm((previous) => ({
      ...previous,
      correspondenceAddressLine1:
        previous.permanentAddressLine1,
      correspondenceAddressLine2:
        previous.permanentAddressLine2,
      correspondenceCity:
        previous.permanentCity,
      correspondenceState:
        previous.permanentState,
      correspondencePincode:
        previous.permanentPincode,
      correspondenceCountry:
        previous.permanentCountry,
    }));

    setCorrespondenceCountryId(
      permanentCountryId
    );
    setCorrespondenceStateId(
      permanentStateId
    );
    setCorrespondenceDistrictId(
      permanentDistrictId
    );

    setCorrespondenceStates(
      permanentStates
    );
    setCorrespondenceDistricts(
      permanentDistricts
    );
    setCorrespondenceCities(
      permanentCities
    );
  };

  /**
   * Keep correspondence address synced while the
   * checkbox is enabled.
   */
  useEffect(() => {
    if (!sameAsPermanent) {
      return;
    }

    setForm((previous) => ({
      ...previous,
      correspondenceAddressLine1:
        previous.permanentAddressLine1,
      correspondenceAddressLine2:
        previous.permanentAddressLine2,
      correspondenceCity:
        previous.permanentCity,
      correspondenceState:
        previous.permanentState,
      correspondencePincode:
        previous.permanentPincode,
      correspondenceCountry:
        previous.permanentCountry,
    }));

    setCorrespondenceCountryId(
      permanentCountryId
    );
    setCorrespondenceStateId(
      permanentStateId
    );
    setCorrespondenceDistrictId(
      permanentDistrictId
    );

    setCorrespondenceStates(
      permanentStates
    );
    setCorrespondenceDistricts(
      permanentDistricts
    );
    setCorrespondenceCities(
      permanentCities
    );
  }, [
    sameAsPermanent,
    permanentCountryId,
    permanentStateId,
    permanentDistrictId,
    permanentStates,
    permanentDistricts,
    permanentCities,
    form.permanentAddressLine1,
    form.permanentAddressLine2,
    form.permanentCity,
    form.permanentState,
    form.permanentPincode,
    form.permanentCountry,
  ]);

  /**
   * ==========================================================
   * OPTIONS
   * ==========================================================
   */

  const managerOptions = useMemo(() => {
    const options = Array.isArray(managers)
      ? managers
          .filter((manager) => {
            if (!item) {
              return true;
            }

            return (
              String(manager?.id) !==
              String(item?.id)
            );
          })
          .map((manager) => ({
            value: String(manager.id),
            label:
              manager.user?.name ||
              manager.name ||
              manager.employeeCode ||
              `Employee ${manager.id}`,
          }))
      : [];

    return [
      {
        value: "",
        label: "Select Manager",
      },
      ...options,
    ];
  }, [managers, item]);

  const roleOptions = useMemo(() => {
    const options = Array.isArray(roles)
      ? roles
          .filter(Boolean)
          .map((role) => ({
            value: String(
              role.slug ||
                role.roleSlug ||
                role.code ||
                role.id
            ),
            label:
              role.name ||
              role.title ||
              role.label ||
              role.slug ||
              role.roleSlug ||
              `Role ${role.id}`,
          }))
      : [];

    return [
      {
        value: "",
        label: "Select Role",
      },
      ...options,
    ];
  }, [roles]);

  const headquarterOptions = useMemo(
    () => [
      {
        value: "",
        label: "Select Headquarter",
      },
      ...(Array.isArray(headquarters)
        ? headquarters.map(
            (headquarter) => ({
              value: String(
                headquarter.id
              ),
              label: headquarter.name,
            })
          )
        : []),
    ],
    [headquarters]
  );

  const workingStationOptions = useMemo(
    () => [
      {
        value: "",
        label: "Select Working Station",
      },
      ...(Array.isArray(
        workingStations
      )
        ? workingStations.map(
            (station) => ({
              value: String(
                station.id
              ),
              label: station.name,
            })
          )
        : []),
    ],
    [workingStations]
  );

  const stayPointOptions = useMemo(
    () => [
      {
        value: "",
        label: "Select Stay Point",
      },
      ...(Array.isArray(stayPoints)
        ? stayPoints.map(
            (stayPoint) => ({
              value: String(
                stayPoint.id
              ),
              label: stayPoint.name,
            })
          )
        : []),
    ],
    [stayPoints]
  );

  const territoryOptions = useMemo(
    () =>
      Array.isArray(territories)
        ? territories.map(
            (territory) => ({
              value: String(
                territory.id
              ),
              label: territory.name,
            })
          )
        : [],
    [territories]
  );

  const countryOptions = useMemo(
    () => [
      {
        value: "",
        label: "Select Country",
      },
      ...(Array.isArray(countries)
        ? countries.map(
            (country) => ({
              value: String(
                country.id
              ),
              label: country.name,
            })
          )
        : []),
    ],
    [countries]
  );

  const permanentStateOptions =
    useMemo(
      () => [
        {
          value: "",
          label: "Select State",
        },
        ...(Array.isArray(
          permanentStates
        )
          ? permanentStates.map(
              (state) => ({
                value: String(
                  state.id
                ),
                label: state.name,
              })
            )
          : []),
      ],
      [permanentStates]
    );

  const permanentDistrictOptions =
    useMemo(
      () => [
        {
          value: "",
          label: "Select District",
        },
        ...(Array.isArray(
          permanentDistricts
        )
          ? permanentDistricts.map(
              (district) => ({
                value: String(
                  district.id
                ),
                label: district.name,
              })
            )
          : []),
      ],
      [permanentDistricts]
    );

  const permanentCityOptions = useMemo(
    () => [
      {
        value: "",
        label: "Select City",
      },
      ...(Array.isArray(
        permanentCities
      )
        ? permanentCities.map(
            (city) => ({
              value: String(
                city.id
              ),
              label: city.name,
            })
          )
        : []),
    ],
    [permanentCities]
  );

  const correspondenceStateOptions =
    useMemo(
      () => [
        {
          value: "",
          label: "Select State",
        },
        ...(Array.isArray(
          correspondenceStates
        )
          ? correspondenceStates.map(
              (state) => ({
                value: String(
                  state.id
                ),
                label: state.name,
              })
            )
          : []),
      ],
      [correspondenceStates]
    );

  const correspondenceDistrictOptions =
    useMemo(
      () => [
        {
          value: "",
          label: "Select District",
        },
        ...(Array.isArray(
          correspondenceDistricts
        )
          ? correspondenceDistricts.map(
              (district) => ({
                value: String(
                  district.id
                ),
                label: district.name,
              })
            )
          : []),
      ],
      [correspondenceDistricts]
    );

  const correspondenceCityOptions =
    useMemo(
      () => [
        {
          value: "",
          label: "Select City",
        },
        ...(Array.isArray(
          correspondenceCities
        )
          ? correspondenceCities.map(
              (city) => ({
                value: String(
                  city.id
                ),
                label: city.name,
              })
            )
          : []),
      ],
      [correspondenceCities]
    );

  /**
   * ==========================================================
   * EMPLOYEE SUBMIT
   * ==========================================================
   */

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (loading) {
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),

        phone:
          form.phone?.trim() || undefined,

        employeeCode:
          form.employeeCode.trim(),

        designation:
          form.designation.trim(),

        roleSlug:
          form.roleSlug || undefined,

        reportingManagerId:
          form.reportingManagerId ||
          undefined,

        headquarterId:
          form.headquarterId || undefined,

        workingStationId:
          form.workingStationId ||
          undefined,

        stayPointId:
          form.stayPointId || undefined,

        territoryIds:
          Array.isArray(form.territoryIds)
            ? form.territoryIds.map(
                (id) => String(id)
              )
            : [],

        joiningDate:
          form.joiningDate || undefined,

        leavingDate:
          form.leavingDate || undefined,

        aadhaarNumber:
          form.aadhaarNumber?.trim() ||
          undefined,

        // Permanent Address
        permanentAddressLine1:
          form.permanentAddressLine1?.trim() ||
          undefined,

        permanentAddressLine2:
          form.permanentAddressLine2?.trim() ||
          undefined,

        permanentCity:
          form.permanentCity?.trim() ||
          undefined,

        permanentState:
          form.permanentState?.trim() ||
          undefined,

        permanentPincode:
          form.permanentPincode?.trim() ||
          undefined,

        permanentCountry:
          form.permanentCountry?.trim() ||
          undefined,

        // Correspondence Address
        correspondenceAddressLine1:
          form.correspondenceAddressLine1?.trim() ||
          undefined,

        correspondenceAddressLine2:
          form.correspondenceAddressLine2?.trim() ||
          undefined,

        correspondenceCity:
          form.correspondenceCity?.trim() ||
          undefined,

        correspondenceState:
          form.correspondenceState?.trim() ||
          undefined,

        correspondencePincode:
          form.correspondencePincode?.trim() ||
          undefined,

        correspondenceCountry:
          form.correspondenceCountry?.trim() ||
          undefined,

        // TA / DA
        daLocalWorking:
          form.daLocalWorking !== ""
            ? Number(form.daLocalWorking)
            : undefined,

        daOutstationFooding:
          form.daOutstationFooding !== ""
            ? Number(
                form.daOutstationFooding
              )
            : undefined,

        daOutstationLodging:
          form.daOutstationLodging !== ""
            ? Number(
                form.daOutstationLodging
              )
            : undefined,

        daOutstationSameDayReturn:
          form.daOutstationSameDayReturn !==
          ""
            ? Number(
                form.daOutstationSameDayReturn
              )
            : undefined,

        localTwoWheelerPerKm:
          form.localTwoWheelerPerKm !== ""
            ? Number(
                form.localTwoWheelerPerKm
              )
            : undefined,

        outstationTwoWheelerPerKm:
          form.outstationTwoWheelerPerKm !==
          ""
            ? Number(
                form.outstationTwoWheelerPerKm
              )
            : undefined,

        localCarPerKm:
          form.localCarPerKm !== ""
            ? Number(form.localCarPerKm)
            : undefined,

        outstationCarPerKm:
          form.outstationCarPerKm !== ""
            ? Number(
                form.outstationCarPerKm
              )
            : undefined,

        mobileInternetMonthly:
          form.mobileInternetMonthly !==
          ""
            ? Number(
                form.mobileInternetMonthly
              )
            : undefined,

        status: form.status,
      };

      if (form.password?.trim()) {
        payload.password =
          form.password.trim();
      }

      if (isEdit) {
        await updateEmployee(
          item.id,
          payload
        );

        showSuccess(
          "Employee updated successfully."
        );
      } else {
        await createEmployee(payload);

        showSuccess(
          "Employee created successfully."
        );
      }

      await reload();
      onClose();
    } catch (error) {
      console.error(
        "Employee save error:",
        error
      );

      showError(
        error?.response?.data?.message ||
          "Unable to save employee."
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * ==========================================================
   * DOCUMENT HANDLERS
   * ==========================================================
   */

  const handleDocumentFileChange = (
    event
  ) => {
    const file =
      event.target.files?.[0] || null;

    setDocumentForm((previous) => ({
      ...previous,
      file,
    }));
  };

  const handleDocumentFieldChange = (
    event
  ) => {
    const { name, value } = event.target;

    setDocumentForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const validateDocumentFile = (file) => {
    if (!file) {
      showError(
        "Please select a document file."
      );
      return false;
    }

    if (file.size > MAX_DOCUMENT_SIZE) {
      showError(
        "Document size must be 10 MB or less."
      );
      return false;
    }

    if (
      !ALLOWED_DOCUMENT_MIME_TYPES.includes(
        file.type
      )
    ) {
      showError(
        "Only JPG, JPEG, PNG, WEBP or PDF files are allowed."
      );
      return false;
    }

    return true;
  };

  const handleDocumentUpload = async () => {
    if (!item?.id) {
      showError(
        "Please save the employee before uploading documents."
      );
      return;
    }

    if (
      !validateDocumentFile(
        documentForm.file
      )
    ) {
      return;
    }

    if (documentUploading) {
      return;
    }

    try {
      setDocumentUploading(true);

      await uploadEmployeeDocument(
        item.id,
        {
          file: documentForm.file,
          documentType:
            documentForm.documentType,
          documentNumber:
            documentForm.documentNumber?.trim() ||
            "",
          remarks:
            documentForm.remarks?.trim() ||
            "",
        }
      );

      showSuccess(
        "Employee document uploaded successfully."
      );

      setDocumentForm({
        ...DEFAULT_DOCUMENT_FORM,
      });

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      await loadDocuments(item.id);
    } catch (error) {
      console.error(
        "Document upload error:",
        error
      );

      showError(
        error?.response?.data?.message ||
          "Unable to upload document."
      );
    } finally {
      setDocumentUploading(false);
    }
  };

  const handleDocumentDelete = async (
    documentId
  ) => {
    if (!item?.id || !documentId) {
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to delete this document?"
    );

    if (!confirmed) {
      return;
    }

    try {
      await deleteEmployeeDocument(
        item.id,
        documentId
      );

      showSuccess(
        "Document deleted successfully."
      );

      await loadDocuments(item.id);
    } catch (error) {
      console.error(
        "Document delete error:",
        error
      );

      showError(
        error?.response?.data?.message ||
          "Unable to delete document."
      );
    }
  };

  const handleDocumentView = (
    document
  ) => {
    const source =
      document?.url ||
      document?.filePath;

    if (!source) {
      showError(
        "Document URL is not available."
      );
      return;
    }

    try {
      const documentUrl =
        getEmployeeDocumentUrl(source);

      if (!documentUrl) {
        showError(
          "Document URL is not available."
        );
        return;
      }

      window.open(
        documentUrl,
        "_blank",
        "noopener,noreferrer"
      );
    } catch (error) {
      console.error(
        "Document view error:",
        error
      );

      showError(
        "Unable to open document."
      );
    }
  };

  const resetDocumentForm = () => {
    setDocumentForm({
      ...DEFAULT_DOCUMENT_FORM,
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /**
   * ==========================================================
   * RENDER
   * ==========================================================
   */

  if (!open) {
    return null;
  }

  return (
    <CrudModal
      open={open}
      title={
        isEdit
          ? "Edit Employee"
          : "Add Employee"
      }
      loading={loading}
      submitText={
        isEdit ? "Update" : "Save"
      }
      onClose={onClose}
      onSubmit={handleSubmit}
      width="max-w-6xl"
    >
      {dropdownLoading && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
          Loading employee options...
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {/* ====================================================
            BASIC INFORMATION
        ==================================================== */}

        <CrudInput
          label="Name"
          name="name"
          value={form.name}
          onChange={handleChange}
          required
        />

        <CrudInput
          label="Email"
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          required
        />

        <CrudInput
          label="Phone"
          name="phone"
          value={form.phone}
          onChange={handleChange}
        />

        <CrudInput
          label="Employee Code"
          name="employeeCode"
          value={form.employeeCode}
          onChange={handleChange}
          required
        />

        <CrudInput
          label="Designation"
          name="designation"
          value={form.designation}
          onChange={handleChange}
          required
        />

        <CrudSelect
          label="Role"
          name="roleSlug"
          value={form.roleSlug}
          onChange={handleChange}
          options={roleOptions}
        />

        <CrudSelect
          label="Reporting Manager"
          name="reportingManagerId"
          value={form.reportingManagerId}
          onChange={handleChange}
          options={managerOptions}
        />

        <CrudSelect
          label="Headquarter"
          name="headquarterId"
          value={form.headquarterId}
          onChange={handleChange}
          options={headquarterOptions}
        />

        <CrudSelect
          label="Working Station"
          name="workingStationId"
          value={form.workingStationId}
          onChange={handleChange}
          options={workingStationOptions}
        />

        <CrudSelect
          label="Stay Point"
          name="stayPointId"
          value={form.stayPointId}
          onChange={handleChange}
          options={stayPointOptions}
        />

        {/* ====================================================
            TERRITORIES
        ==================================================== */}

        <div className="md:col-span-2">
          <label className="mb-2 block text-sm font-semibold text-navy-700">
            Territory
          </label>

          <select
            multiple
            value={form.territoryIds}
            onChange={handleTerritoryChange}
            disabled={dropdownLoading}
            className="min-h-[130px] w-full rounded-xl border border-gray-300 bg-white px-3 py-3 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          >
            {territoryOptions.map(
              (option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              )
            )}
          </select>

          <p className="mt-1 text-xs text-gray-500">
            Hold Ctrl / Command to select
            multiple territories.
          </p>
        </div>

        {/* ====================================================
            PERMANENT ADDRESS
        ==================================================== */}

        <div className="md:col-span-2">
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-5">
              <h3 className="text-base font-bold text-navy-700">
                Permanent Address
              </h3>

              <p className="mt-1 text-xs text-gray-500">
                Select location from the existing
                Country → State → District → City
                masters.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <CrudInput
                  label="Address Line 1"
                  name="permanentAddressLine1"
                  value={
                    form.permanentAddressLine1
                  }
                  onChange={handleChange}
                />
              </div>

              <div className="md:col-span-2">
                <CrudInput
                  label="Address Line 2"
                  name="permanentAddressLine2"
                  value={
                    form.permanentAddressLine2
                  }
                  onChange={handleChange}
                />
              </div>

              <CrudSelect
                label="Country"
                name="permanentCountryId"
                value={permanentCountryId}
                onChange={
                  handlePermanentCountryChange
                }
                options={countryOptions}
                disabled={dropdownLoading}
              />

              <CrudSelect
                label="State"
                name="permanentStateId"
                value={permanentStateId}
                onChange={
                  handlePermanentStateChange
                }
                options={permanentStateOptions}
                disabled={
                  !permanentCountryId ||
                  addressLoading
                }
              />

              <CrudSelect
                label="District"
                name="permanentDistrictId"
                value={permanentDistrictId}
                onChange={
                  handlePermanentDistrictChange
                }
                options={
                  permanentDistrictOptions
                }
                disabled={
                  !permanentStateId ||
                  addressLoading
                }
              />

              <CrudSelect
                label="City"
                name="permanentCityId"
                value={
                  findByName(
                    permanentCities,
                    form.permanentCity
                  )?.id
                    ? String(
                        findByName(
                          permanentCities,
                          form.permanentCity
                        ).id
                      )
                    : ""
                }
                onChange={
                  handlePermanentCityChange
                }
                options={
                  permanentCityOptions
                }
                disabled={
                  !permanentDistrictId ||
                  addressLoading
                }
              />

              <CrudInput
                label="Pincode"
                name="permanentPincode"
                value={form.permanentPincode}
                onChange={handleChange}
                maxLength={10}
              />
            </div>
          </div>
        </div>

        {/* ====================================================
            CORRESPONDENCE ADDRESS
        ==================================================== */}

        <div className="md:col-span-2">
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-base font-bold text-navy-700">
                  Correspondence Address
                </h3>

                <p className="mt-1 text-xs text-gray-500">
                  Address used for communication
                  and official correspondence.
                </p>
              </div>

              <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={sameAsPermanent}
                  onChange={
                    handleSameAsPermanent
                  }
                  className="h-4 w-4 rounded border-gray-300"
                />
                Same as Permanent Address
              </label>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <CrudInput
                  label="Address Line 1"
                  name="correspondenceAddressLine1"
                  value={
                    form.correspondenceAddressLine1
                  }
                  onChange={handleChange}
                  disabled={sameAsPermanent}
                />
              </div>

              <div className="md:col-span-2">
                <CrudInput
                  label="Address Line 2"
                  name="correspondenceAddressLine2"
                  value={
                    form.correspondenceAddressLine2
                  }
                  onChange={handleChange}
                  disabled={sameAsPermanent}
                />
              </div>

              <CrudSelect
                label="Country"
                name="correspondenceCountryId"
                value={
                  correspondenceCountryId
                }
                onChange={
                  handleCorrespondenceCountryChange
                }
                options={countryOptions}
                disabled={
                  sameAsPermanent ||
                  dropdownLoading
                }
              />

              <CrudSelect
                label="State"
                name="correspondenceStateId"
                value={
                  correspondenceStateId
                }
                onChange={
                  handleCorrespondenceStateChange
                }
                options={
                  correspondenceStateOptions
                }
                disabled={
                  sameAsPermanent ||
                  !correspondenceCountryId ||
                  addressLoading
                }
              />

              <CrudSelect
                label="District"
                name="correspondenceDistrictId"
                value={
                  correspondenceDistrictId
                }
                onChange={
                  handleCorrespondenceDistrictChange
                }
                options={
                  correspondenceDistrictOptions
                }
                disabled={
                  sameAsPermanent ||
                  !correspondenceStateId ||
                  addressLoading
                }
              />

              <CrudSelect
                label="City"
                name="correspondenceCityId"
                value={
                  findByName(
                    correspondenceCities,
                    form.correspondenceCity
                  )?.id
                    ? String(
                        findByName(
                          correspondenceCities,
                          form.correspondenceCity
                        ).id
                      )
                    : ""
                }
                onChange={
                  handleCorrespondenceCityChange
                }
                options={
                  correspondenceCityOptions
                }
                disabled={
                  sameAsPermanent ||
                  !correspondenceDistrictId ||
                  addressLoading
                }
              />

              <CrudInput
                label="Pincode"
                name="correspondencePincode"
                value={
                  form.correspondencePincode
                }
                onChange={handleChange}
                maxLength={10}
                disabled={sameAsPermanent}
              />
            </div>
          </div>
        </div>

        {/* ====================================================
            TA / DA
        ==================================================== */}

        <div className="md:col-span-2">
          <div className="rounded-2xl border border-blue-100 bg-blue-50/50 p-5">
            <div className="mb-5">
              <h3 className="text-base font-bold text-navy-700">
                TA / DA & Reimbursement Policy
              </h3>

              <p className="mt-1 text-xs text-gray-500">
                These employee-specific rates are
                used by the expense / TA-DA flow
                as the default eligible amount.
              </p>
            </div>

            <div className="mb-6">
              <h4 className="mb-3 text-sm font-bold text-gray-700">
                Daily Allowance (DA)
              </h4>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                <CrudInput
                  label="Local Working DA (₹ / Day)"
                  name="daLocalWorking"
                  type="number"
                  min="0"
                  step="0.01"
                  value={
                    form.daLocalWorking
                  }
                  onChange={handleChange}
                />

                <CrudInput
                  label="Outstation Fooding (₹ / Day)"
                  name="daOutstationFooding"
                  type="number"
                  min="0"
                  step="0.01"
                  value={
                    form.daOutstationFooding
                  }
                  onChange={handleChange}
                />

                <CrudInput
                  label="Outstation Lodging (₹ / Day)"
                  name="daOutstationLodging"
                  type="number"
                  min="0"
                  step="0.01"
                  value={
                    form.daOutstationLodging
                  }
                  onChange={handleChange}
                />

                <CrudInput
                  label="Same Day Return (₹)"
                  name="daOutstationSameDayReturn"
                  type="number"
                  min="0"
                  step="0.01"
                  value={
                    form.daOutstationSameDayReturn
                  }
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="mb-6">
              <h4 className="mb-3 text-sm font-bold text-gray-700">
                Conveyance / Petrol
              </h4>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                <CrudInput
                  label="Local Two Wheeler (₹ / KM)"
                  name="localTwoWheelerPerKm"
                  type="number"
                  min="0"
                  step="0.01"
                  value={
                    form.localTwoWheelerPerKm
                  }
                  onChange={handleChange}
                />

                <CrudInput
                  label="Outstation Two Wheeler (₹ / KM)"
                  name="outstationTwoWheelerPerKm"
                  type="number"
                  min="0"
                  step="0.01"
                  value={
                    form.outstationTwoWheelerPerKm
                  }
                  onChange={handleChange}
                />

                <CrudInput
                  label="Local Car (₹ / KM)"
                  name="localCarPerKm"
                  type="number"
                  min="0"
                  step="0.01"
                  value={
                    form.localCarPerKm
                  }
                  onChange={handleChange}
                />

                <CrudInput
                  label="Outstation Car (₹ / KM)"
                  name="outstationCarPerKm"
                  type="number"
                  min="0"
                  step="0.01"
                  value={
                    form.outstationCarPerKm
                  }
                  onChange={handleChange}
                />
              </div>

              <p className="mt-2 text-xs text-gray-500">
                Car reimbursement is intended for
                sampling / eligible company policy
                usage.
              </p>
            </div>

            <div>
              <h4 className="mb-3 text-sm font-bold text-gray-700">
                Monthly Reimbursement
              </h4>

              <CrudInput
                label="Mobile Bill + Internet (₹ / Month)"
                name="mobileInternetMonthly"
                type="number"
                min="0"
                step="0.01"
                value={
                  form.mobileInternetMonthly
                }
                onChange={handleChange}
              />
            </div>

            <div className="mt-5 rounded-xl border border-gray-200 bg-white p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Current Policy Summary
              </p>

              <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
                <div>
                  <span className="text-xs text-gray-500">
                    Local DA
                  </span>
                  <p className="font-semibold text-gray-800">
                    ₹{form.daLocalWorking || "0"} / day
                  </p>
                </div>

                <div>
                  <span className="text-xs text-gray-500">
                    Outstation
                  </span>
                  <p className="font-semibold text-gray-800">
                    ₹
                    {form.daOutstationFooding ||
                      "0"}{" "}
                    + ₹
                    {form.daOutstationLodging ||
                      "0"}
                  </p>
                </div>

                <div>
                  <span className="text-xs text-gray-500">
                    Two Wheeler
                  </span>
                  <p className="font-semibold text-gray-800">
                    Local ₹
                    {form.localTwoWheelerPerKm ||
                      "0"}{" "}
                    / KM
                  </p>
                </div>

                <div>
                  <span className="text-xs text-gray-500">
                    Car
                  </span>
                  <p className="font-semibold text-gray-800">
                    Local ₹
                    {form.localCarPerKm ||
                      "0"}{" "}
                    / KM
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ====================================================
            DATES / ID / STATUS
        ==================================================== */}

        <CrudInput
          label="Joining Date"
          type="date"
          name="joiningDate"
          value={form.joiningDate}
          onChange={handleChange}
        />

        <CrudInput
          label="Leaving Date"
          type="date"
          name="leavingDate"
          value={form.leavingDate}
          onChange={handleChange}
        />

        <CrudInput
          label="Aadhaar Number"
          name="aadhaarNumber"
          value={form.aadhaarNumber}
          onChange={handleChange}
          maxLength={12}
        />

        <CrudSelect
          label="Status"
          name="status"
          value={form.status}
          onChange={handleChange}
          options={EMPLOYEE_STATUSES}
        />

        {/* ====================================================
            PASSWORD
        ==================================================== */}

        {!isEdit && (
          <div className="md:col-span-2">
            <CrudInput
              label="Password"
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
            />
          </div>
        )}

        {isEdit && (
          <div className="md:col-span-2">
            <CrudInput
              label="New Password (optional)"
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
            />
          </div>
        )}

        {/* ====================================================
            DOCUMENT MANAGEMENT
        ==================================================== */}

        {isEdit && (
          <div className="md:col-span-2 border-t border-gray-200 pt-6">
            <div className="mb-4">
              <h3 className="text-base font-bold text-navy-700">
                Aadhaar / Document Details
              </h3>

              <p className="mt-1 text-xs text-gray-500">
                Upload Aadhaar, PAN, ID proof,
                address proof, joining documents
                or other employee documents.
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <CrudSelect
                  label="Document Type"
                  name="documentType"
                  value={
                    documentForm.documentType
                  }
                  onChange={
                    handleDocumentFieldChange
                  }
                  options={DOCUMENT_TYPES}
                />

                <CrudInput
                  label="Document Number"
                  name="documentNumber"
                  value={
                    documentForm.documentNumber
                  }
                  onChange={
                    handleDocumentFieldChange
                  }
                />

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-semibold text-navy-700">
                    Upload File
                  </label>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={DOCUMENT_ACCEPT}
                    onChange={
                      handleDocumentFileChange
                    }
                    disabled={
                      documentUploading
                    }
                    className="block w-full rounded-xl border border-gray-300 bg-white px-3 py-3 text-sm"
                  />

                  <p className="mt-1 text-xs text-gray-500">
                    JPG, JPEG, PNG, WEBP or PDF.
                    Maximum 10 MB.
                  </p>

                  {documentForm.file && (
                    <p className="mt-2 text-xs font-medium text-gray-600">
                      Selected:{" "}
                      {
                        documentForm.file
                          .name
                      }
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <CrudInput
                    label="Remarks"
                    name="remarks"
                    value={
                      documentForm.remarks
                    }
                    onChange={
                      handleDocumentFieldChange
                    }
                  />
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  disabled={
                    documentUploading ||
                    !documentForm.file
                  }
                  onClick={
                    handleDocumentUpload
                  }
                  className="rounded-xl bg-brand-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {documentUploading
                    ? "Uploading..."
                    : "Upload Document"}
                </button>

                <button
                  type="button"
                  disabled={
                    documentUploading
                  }
                  onClick={
                    resetDocumentForm
                  }
                  className="rounded-xl border border-gray-300 bg-white px-5 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="mt-5">
              <h4 className="mb-3 text-sm font-bold text-navy-700">
                Uploaded Documents
              </h4>

              {documentLoading ? (
                <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-500">
                  Loading documents...
                </div>
              ) : documents.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-white p-4 text-sm text-gray-500">
                  No documents uploaded yet.
                </div>
              ) : (
                <div className="space-y-3">
                  {documents.map(
                    (document) => (
                      <div
                        key={document.id}
                        className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 md:flex-row md:items-center md:justify-between"
                      >
                        <div className="min-w-0">
                          <p className="font-semibold capitalize text-navy-700">
                            {String(
                              document.documentType ||
                                "document"
                            ).replace(
                              /_/g,
                              " "
                            )}
                          </p>

                          <p className="mt-1 truncate text-xs text-gray-500">
                            {
                              document.originalName ||
                              document.fileName ||
                              "-"
                            }
                          </p>

                          {document.documentNumber && (
                            <p className="mt-1 text-xs text-gray-500">
                              No:{" "}
                              {
                                document.documentNumber
                              }
                            </p>
                          )}

                          {document.remarks && (
                            <p className="mt-1 text-xs text-gray-500">
                              {
                                document.remarks
                              }
                            </p>
                          )}
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                          {(
                            document.url ||
                            document.filePath
                          ) && (
                            <button
                              type="button"
                              onClick={() =>
                                handleDocumentView(
                                  document
                                )
                              }
                              className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-100"
                            >
                              View
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() =>
                              handleDocumentDelete(
                                document.id
                              )
                            }
                            disabled={
                              documentUploading
                            }
                            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </CrudModal>
  );
}
